import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { SignupFormLocation } from "@redotech/redo-marketing-common/schemas/submit-signup-form-step-schema";
import { getButtonStyles } from "@redotech/redo-model/brand-kit/utils";
import {
  DEFAULT_DROPOFF_PERCENTAGE_TRIGGER,
  RESCUE_MODAL_UI_BUTTON_SUBTEXT_DEFAULT,
  RescueModalDiscount,
} from "@redotech/redo-model/checkouts/predictive-conversion-settings";
import {
  DefaultCheckoutProbabilityBucket,
  getDefaultCheckoutProbabilityBucket,
} from "@redotech/redo-model/checkouts/segments";
import {
  AutocheckRescueModalConfiguration,
  AutocheckRescuePanelActiveSplit,
} from "@redotech/redo-model/conversion/conversion-autocheck";
import { RESCUE_PANEL_CTA_CLICKED_KEY } from "@redotech/redo-model/shopify/cart";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  SpotlightForm,
  SpotlightTriggerType,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import { RescuePanelContent } from "@redotech/redo-web/conversion/storefront-widgets/rescue-panel-content";
import { SpotlightModal } from "@redotech/redo-web/marketing/spotlight/spotlight-modal";
import {
  SpotlightClient,
  SpotlightLocalData,
} from "@redotech/redo-web/marketing/spotlight/types";
import {
  getNextStepIndex,
  isOnMobile,
  submitSpotlightStep,
  useLocalStorage,
} from "@redotech/redo-web/marketing/spotlight/utils";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  ANONYMOUS_SHOPPER_ID_KEY,
  RESCUE_PANEL_PREVIEW_QUERY_PARAM,
} from "@redotech/redo-web/utils/shared-conf";
import {
  ShopifyCart,
  ShopifyCartAttributes,
} from "@redotech/shopify-client/ajax";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { getCustomFieldOptions } from "@redotech/shopify-sdk/rpc/schema/marketing-embedded-form/get-custom-field-options";
import { generateRescueDiscountCode } from "@redotech/shopify-sdk/rpc/schema/rescue-panel/generate-rescue-discount-code";
import { logRescuePanelAnalytics } from "@redotech/shopify-sdk/rpc/schema/rescue-panel/log-rescue-panel-analytics";
import { getSpotlight } from "@redotech/shopify-sdk/rpc/schema/spotlight/get-spotlight";
import { logSpotlightAnalytics } from "@redotech/shopify-sdk/rpc/schema/spotlight/log-spotlight-analytics";
import { submitSpotlightStep as submitSpotlightStepRpc } from "@redotech/shopify-sdk/rpc/schema/spotlight/submit-spotlight-step";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ShopifyExtensionClientContext } from "../../client/client-context";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { StorefrontClient } from "../../client/storefront";
import { getInternalAppStatusEmitter } from "../../config";
import { log } from "../../monitoring/log";
import {
  safeCartAdd,
  safeUpdateCartAttribute,
  useCart,
} from "../../utils/cart";
import {
  getCoverageCartAttributes,
  goToCheckout,
} from "../../utils/checkout-buttons/on-click-logic";
import { RedoCheckoutButtonClickedEvent } from "../../utils/internal-app-events";
import { CheckoutProbabilityContext } from "./checkout-probability-context";
import { useRescuePanelCoverage } from "./use-rescue-panel-coverage";
import { useRescuePanelSplit } from "./use-rescue-panel-split";

export const RescuePanel = memo(function RescuePanel({
  storefrontClient,
  discountCode,
  discount,
  autocheck,
  autocheckRescuePanelSplit,
  dropoffPercentageTrigger = DEFAULT_DROPOFF_PERCENTAGE_TRIGGER,
  secondsInColdTrigger,
  rescueModalUi,
}: {
  storefrontClient: StorefrontClient;
  discountCode: string | undefined;
  discount: RescueModalDiscount | undefined;
  autocheck: WidgetAutocheck;
  autocheckRescuePanelSplit: AutocheckRescuePanelActiveSplit | null;
  dropoffPercentageTrigger?: number;
  secondsInColdTrigger?: number;
  rescueModalUi: AutocheckRescueModalConfiguration["ui"];
}) {
  const checkoutProbabilityContext = useContext(CheckoutProbabilityContext);
  const shopifyRpcClient = useContext(RedoShopifyRpcClientContext);
  const shopifyExtensionClient = useRequiredContext(
    ShopifyExtensionClientContext,
  );

  const rescuePanelSplit = useRescuePanelSplit({
    autocheckRescuePanelSplit,
    storefrontClient,
  });

  const previewOn =
    new URLSearchParams(window.location.search).get(
      RESCUE_PANEL_PREVIEW_QUERY_PARAM,
    ) === "true";

  const [panelOpen, setPanelOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(
    () =>
      getLocalStorageWithExpiry(SHOWN_LOCAL_STORAGE_KEY) ===
      SHOWN_LOCAL_STORAGE_VALUE,
  );

  const mountTimeRef = useRef<Temporal.Instant>(Temporal.Now.instant());

  // Spotlight state for intent-triggered spotlights
  const [spotlight, setSpotlight] = useState<SpotlightForm | null>(null);
  const [spotlightId, setSpotlightId] = useState<string | null>(null);
  const [treatmentId, setTreatmentId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightCompleted, setSpotlightCompleted] = useState(false);
  const [resolvedDiscounts, setResolvedDiscounts] = useState<
    Record<string, string> | undefined
  >();
  const spotlightLocalStorageKey = "redo.spotlight.rescue";
  const [spotlightLocalData, setSpotlightLocalData] =
    useLocalStorage<SpotlightLocalData>(spotlightLocalStorageKey, {});

  const shopifyCustomerId = useMemo(
    () =>
      (
        globalThis as any
      ).ShopifyAnalytics?.meta?.page?.customerId?.toString() ?? null,
    [],
  );

  const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

  // Create SpotlightClient from RPC client
  const spotlightClient: SpotlightClient | undefined = useMemo(() => {
    if (!shopifyRpcClient) {
      return undefined;
    }
    return {
      getSpotlight: async (params, options) =>
        shopifyRpcClient(getSpotlight, params, options),
      getCustomFieldOptions: async (params) =>
        shopifyRpcClient(getCustomFieldOptions, params),
      logSpotlightAnalytics: async (params) =>
        shopifyRpcClient(logSpotlightAnalytics, params),
      submitSpotlightStep: async (params) =>
        shopifyRpcClient(submitSpotlightStepRpc, params),
    } satisfies SpotlightClient;
  }, [shopifyRpcClient]);

  const location = useMemo(
    () =>
      globalThis.redoStorefront?.type === "commentsold"
        ? SignupFormLocation.COMMENTSOLD_STOREFRONT
        : SignupFormLocation.SHOPIFY_STOREFRONT,
    [],
  );

  const cart = useCart();

  const { correctVariant, coverageInfoEligibilityLoad } =
    useRescuePanelCoverage({
      cart,
      storefrontClient,
      shopifyExtensionClient,
      autocheck,
    });

  // Shared pre-checkout operations: update cart attributes, add coverage product,
  // log analytics, handle split analytics, and fire internal event.
  // Used by both the rescue panel CTA and the spotlight checkout action.
  const prepareCoverageForCheckoutAndHandleAnalytics = useHandler(async () => {
    const updateCartAttributes = async () => {
      const attributes: ShopifyCartAttributes["attributes"] = [
        { key: RESCUE_PANEL_CTA_CLICKED_KEY, value: "true" },
      ];

      if (correctVariant) {
        const coverageAttributes = getCoverageCartAttributes({
          addCoverage: true,
        });
        attributes.push(...coverageAttributes);
      }

      await safeUpdateCartAttribute({
        client: storefrontClient,
        attributes: { attributes },
        retries: 1,
      });
    };

    const maybeAddCoverageProductToCart = async () => {
      if (!correctVariant) {
        return;
      }

      await safeCartAdd({
        client: storefrontClient,
        items: {
          items: [
            {
              id: correctVariant.id,
              quantity: 1,
              properties: coverageInfoEligibilityLoad.value?.returns.isFinalSale
                ? { __final_sale_coverage: "true" }
                : {},
            },
          ],
        },
        retries: 2,
      });
    };

    const applyAnalytics = async () => {
      if (!shopifyRpcClient) {
        return;
      }

      await logRescuePanelCtaClicked({
        shopifyRpcClient,
        shopifyCustomerId,
        cart,
      });
    };

    const handleSplitAnalytics = async () => {
      if (!rescuePanelSplit.splitActive) {
        return;
      }

      await rescuePanelSplit.onPanelCtaClicked();
    };

    const setCoverageCheckoutButtonClicked = async () => {
      const internalEmitter = getInternalAppStatusEmitter();
      const clickEvent: RedoCheckoutButtonClickedEvent = {
        type: "RedoCheckoutButtonClickedEvent",
        timestamp: Temporal.Now.instant().epochMilliseconds,
        addCoverage: true,
      };
      internalEmitter.emit(clickEvent);
    };

    await Promise.all([
      updateCartAttributes(),
      maybeAddCoverageProductToCart(),
      applyAnalytics(),
      handleSplitAnalytics(),
      setCoverageCheckoutButtonClicked(),
    ]);
  });

  // Handle checkout button click (rescue panel CTA, not spotlight)
  const [checkoutClickLoad, doCheckoutClick] = useTriggerLoad(async (abort) => {
    if (!discount) {
      return;
    }

    try {
      const discountToApply = await (async () => {
        if (!discount.autoGenerateRandomCode) {
          return discountCode;
        }

        if (!shopifyRpcClient) {
          return undefined;
        }

        const response = await shopifyRpcClient(
          generateRescueDiscountCode,
          null,
        );
        if (response.discountCode) {
          return response.discountCode;
        }

        return undefined;
      })();

      if (!discountToApply) {
        throw new Error("Failed to generate rescue discount code");
      }

      await Promise.all([
        storefrontClient.applyDiscountCode(discountToApply, abort),
        prepareCoverageForCheckoutAndHandleAnalytics(),
      ]);

      goToCheckout();
    } catch (error) {
      console.error("Failed to navigate to checkout:", error);
    }
  });

  // Handle show rescue panel - also fetches spotlight if available
  const [_, doShowPanel] = useTriggerLoad(async (abort) => {
    // Determine whether to fetch a spotlight and which one
    const shouldFetchSpotlight = rescuePanelSplit.splitActive
      ? !!rescuePanelSplit.spotlightId // Split active: only fetch if treatment specifies a spotlight
      : true; // No split: use existing behavior (try to find any checkout probability spotlight)

    const spotlightForceId =
      rescuePanelSplit.splitActive && rescuePanelSplit.spotlightId
        ? rescuePanelSplit.spotlightId
        : undefined;

    if (shouldFetchSpotlight && spotlightClient && anonymousId && !spotlight) {
      try {
        const result = await spotlightClient.getSpotlight(
          {
            customerHash: anonymousId,
            shopifyCustomerId,
            isOnMobile: isOnMobile(),
            currentUrl: window.location.href,
            triggerType: SpotlightTriggerType.CHECKOUT_PROBABILITY,
            ...(spotlightForceId ? { forceId: spotlightForceId } : {}),
          },
          { signal: abort },
        );

        if (result.spotlight) {
          setSpotlight(result.spotlight);
          setSpotlightId(result.spotlightId ?? null);
          setTreatmentId(result.treatmentId ?? null);
          setCurrentStep(getNextStepIndex(result.spotlight, 0));

          // Log spotlight view analytics
          if (result.spotlightId && !spotlightLocalData.spotlightViewedAt) {
            const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);
            if (anonymousId) {
              await spotlightClient.logSpotlightAnalytics({
                anonymousId,
                spotlightId: result.spotlightId,
                treatmentId: result.treatmentId ?? null,
                type: "view",
                shopifyCustomerId,
              });
            }
            setSpotlightLocalData((prev) => ({
              ...prev,
              spotlightViewedAt: new Date(),
            }));
          }

          // Also log rescue panel viewed analytics
          if (shopifyRpcClient) {
            await logRescuePanelViewed({
              shopifyRpcClient,
              shopifyCustomerId,
              cart,
            });
          }

          setPanelOpen(true);
          return;
        }
      } catch (err) {
        console.error("Failed to fetch spotlight for rescue panel", err);
      }
    }

    // Fall back to regular rescue panel behavior — only if rescue modal is configured
    if (!discount) {
      return;
    }

    if (shopifyRpcClient) {
      await logRescuePanelViewed({ shopifyRpcClient, shopifyCustomerId, cart });
    }

    setPanelOpen(true);
  });

  const handleCheckoutClick = useHandler(() => {
    doCheckoutClick();
  });

  const onShowPanel = useHandler(() => {
    doShowPanel();
  });

  const onClosePanel = useHandler(() => {
    setPanelOpen(false);
    setHasDismissed(true);

    try {
      setLocalStorageWithExpiry(
        SHOWN_LOCAL_STORAGE_KEY,
        SHOWN_LOCAL_STORAGE_VALUE,
        PANEL_DISMISSED_EXPIRY_HOURS,
      );
    } catch (error) {
      console.warn(
        "Failed to save rescue panel state to local storage:",
        error,
      );
    }
  });

  // Handle spotlight step submission
  const handleSpotlightSubmitStep = useHandler(
    async (i: number, formData: Record<string, string>) => {
      const isFirstSubmission = !spotlightLocalData.atLeastOneStepSubmittedAt;
      const result = await submitSpotlightStep({
        i,
        formData,
        spotlight,
        spotlightId,
        treatmentId,
        client: spotlightClient,
        customerId: shopifyCustomerId,
        shopifyCustomerId,
        setCurrentStep,
        location,
        onLastStepSubmitted: () => {
          setPanelOpen(false);
          setSpotlightCompleted(true);
          setHasDismissed(true);
          try {
            setLocalStorageWithExpiry(
              SHOWN_LOCAL_STORAGE_KEY,
              SHOWN_LOCAL_STORAGE_VALUE,
              PANEL_DISMISSED_EXPIRY_HOURS,
            );
          } catch (error) {
            console.warn(
              "Failed to save rescue panel state to local storage:",
              error,
            );
          }
        },
      });
      if (isFirstSubmission) {
        setSpotlightLocalData((prev) => ({
          ...prev,
          atLeastOneStepSubmittedAt: new Date(),
        }));
      }
      if (result.resolvedDiscounts) {
        setResolvedDiscounts((prev) => ({
          ...prev,
          ...result.resolvedDiscounts,
        }));
      }
    },
  );

  // Determine effective trigger values - use split treatment values when available
  const effectiveDropoffPercentageTrigger =
    rescuePanelSplit.splitActive &&
    rescuePanelSplit.dropoffPercentageTrigger !== undefined
      ? rescuePanelSplit.dropoffPercentageTrigger
      : dropoffPercentageTrigger;

  const effectiveSecondsInColdTrigger =
    rescuePanelSplit.splitActive &&
    rescuePanelSplit.secondsInColdTrigger !== undefined
      ? rescuePanelSplit.secondsInColdTrigger
      : secondsInColdTrigger;

  // Monitor checkout probability changes and show panel when conditions are met
  useEffect(() => {
    if (!checkoutProbabilityContext) {
      return;
    }

    const showPanelParams: ShouldShowPanelParams = {
      hasDismissed,
      cumulativeConversionProbability:
        checkoutProbabilityContext.cumulativeConversionProbability,
      previousCumulativeConversionProbability:
        checkoutProbabilityContext.previousCumulativeConversionProbability,
      instantaneousConversionProbability:
        checkoutProbabilityContext.instantaneousHazardProbability,
      previousInstantaneousConversionProbability:
        checkoutProbabilityContext.previousInstantaneousHazardProbability,
      mountTime: mountTimeRef.current,
      cart,
      previewOn,
      dropoffPercentageTrigger: effectiveDropoffPercentageTrigger,
      secondsInColdTrigger: effectiveSecondsInColdTrigger,
      sessionAgeInMs: checkoutProbabilityContext.sessionAgeInMs,
    };

    if (!shouldShowPanel(showPanelParams)) {
      return;
    }

    if (rescuePanelSplit.splitActive) {
      rescuePanelSplit.onShouldShowPanel()?.catch(console.error);
    }

    onShowPanel();
  }, [
    checkoutProbabilityContext,
    hasDismissed,
    rescuePanelSplit,
    onShowPanel,
    cart,
    previewOn,
    effectiveDropoffPercentageTrigger,
    effectiveSecondsInColdTrigger,
  ]);

  const checkoutButtonText = useMemo(() => {
    switch (rescueModalUi.button.subtext.shouldShow) {
      case "always":
        return rescueModalUi.button.subtext.value;
      case "if_coverage_eligible":
        return correctVariant
          ? rescueModalUi.button.subtext.value ||
              RESCUE_MODAL_UI_BUTTON_SUBTEXT_DEFAULT.value
          : undefined;
      case "never":
        return undefined;
      default:
        assertNever(rescueModalUi.button.subtext);
    }
  }, [rescueModalUi, correctVariant]);

  const buttonStyles = useMemo(() => {
    return getButtonStyles({
      brandKit: autocheck.brandKit,
      hierarchy: "primary",
    });
  }, [autocheck.brandKit]);

  if (
    rescuePanelSplit.splitActive &&
    !rescuePanelSplit.panelAllowedForTreatment &&
    !previewOn
  ) {
    return null;
  }

  // If we have a spotlight with checkoutProbability trigger, render SpotlightModal
  if (spotlight && spotlight.triggers.checkoutProbability?.enabled) {
    const currentStepConfig = spotlight.steps[currentStep];
    const isMobile = isOnMobile();

    return (
      <SpotlightModal
        ariaLabel="Marketing spotlight"
        brandKit={autocheck.brandKit}
        buttonSubtext={checkoutButtonText}
        client={spotlightClient}
        completed={spotlightCompleted}
        currentStep={currentStep}
        desktopConfig={currentStepConfig?.desktop}
        isMobile={isMobile}
        mobileConfig={currentStepConfig?.mobile}
        onCheckoutAction={prepareCoverageForCheckoutAndHandleAnalytics}
        onClose={onClosePanel}
        onSubmitStep={handleSpotlightSubmitStep}
        open={panelOpen}
        resolvedDiscounts={resolvedDiscounts}
        spotlight={spotlight}
      />
    );
  }

  // Default: render RescuePanelContent (only if rescue modal discount is configured)
  if (!discount) {
    return null;
  }

  return (
    <RescuePanelContent
      buttonPending={checkoutClickLoad.pending}
      buttonStyles={buttonStyles}
      buttonSubtext={checkoutButtonText}
      buttonText={rescueModalUi.button.text}
      description={rescueModalUi.description}
      onButtonClick={handleCheckoutClick}
      onClose={onClosePanel}
      open={panelOpen}
      placement={rescueModalUi.placement}
      title={rescueModalUi.title}
    />
  );
});

const VIEWED_LOCAL_STORAGE_KEY_PREFIX = "rrp-viewed";

function constructPanelViewedLocalStorageKey({
  sessionId,
}: {
  sessionId: string;
}): `${typeof VIEWED_LOCAL_STORAGE_KEY_PREFIX}-${string}` {
  return `${VIEWED_LOCAL_STORAGE_KEY_PREFIX}-${sessionId}`;
}

async function logRescuePanelViewed({
  shopifyRpcClient,
  shopifyCustomerId,
  cart,
}: {
  shopifyRpcClient: RedoShopifyRpcClient;
  shopifyCustomerId: string | null;
  cart: ShopifyCart | undefined;
}) {
  const sessionId = getCookie(AllCookies.ShopifySessionId);
  if (!sessionId) {
    console.warn("Session ID not available for recording rescue panel viewed");
    return;
  }

  const localStorageKey = constructPanelViewedLocalStorageKey({ sessionId });
  const hasAlreadyRecorded = getLocalStorageWithExpiry(localStorageKey);
  if (hasAlreadyRecorded) {
    return;
  }

  setLocalStorageWithExpiry(localStorageKey, "true");

  try {
    const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);
    const itemQuantity =
      cart?.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0;

    await shopifyRpcClient(logRescuePanelAnalytics, {
      sessionId,
      type: "viewed",
      shopifyCustomerId,
      anonymousId: anonymousId ?? undefined,
      itemQuantity,
    });
  } catch (err) {
    console.error("Failed to record rescue panel viewed", err);
  }
}

async function logRescuePanelCtaClicked({
  shopifyRpcClient,
  shopifyCustomerId,
  cart,
}: {
  shopifyRpcClient: RedoShopifyRpcClient;
  shopifyCustomerId: string | null;
  cart: ShopifyCart | undefined;
}) {
  const sessionId = getCookie(AllCookies.ShopifySessionId);
  if (!sessionId) {
    return;
  }

  try {
    const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);
    const itemQuantity =
      cart?.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0;

    await shopifyRpcClient(logRescuePanelAnalytics, {
      sessionId,
      type: "clicked",
      shopifyCustomerId,
      anonymousId: anonymousId ?? undefined,
      itemQuantity,
    });
  } catch (err) {
    console.error("Failed to record rescue panel CTA clicked", err);
  }
}

const SHOWN_LOCAL_STORAGE_KEY = "rrp-dismissed";
const SHOWN_LOCAL_STORAGE_VALUE = "true";
const SHOW_DELAY_MS = 3000;
const PANEL_DISMISSED_EXPIRY_HOURS = 24 * 7; // 7 days
const MS_PER_SECOND = 1000;

const STALE_PAGE_TIME_JITTER_SECONDS = 30;
const STALE_PAGE_TIME_SECONDS = 90 - STALE_PAGE_TIME_JITTER_SECONDS / 2;

interface ShouldShowPanelParams {
  hasDismissed: boolean;
  cumulativeConversionProbability: number | null;
  previousCumulativeConversionProbability: number | null;
  instantaneousConversionProbability: number | null;
  previousInstantaneousConversionProbability: number | null;
  mountTime: Temporal.Instant;
  cart: ShopifyCart | undefined;
  previewOn: boolean;
  dropoffPercentageTrigger: number;
  secondsInColdTrigger: number | undefined;
  sessionAgeInMs: number | null;
}

const shouldShowPanel = (params: ShouldShowPanelParams): boolean => {
  if (params.previewOn) {
    console.warn("Showing rescue panel in preview mode");
    return true;
  }

  if (
    params.hasDismissed ||
    !isDef(params.cumulativeConversionProbability) ||
    !isDef(params.instantaneousConversionProbability) ||
    !isDef(params.previousInstantaneousConversionProbability) ||
    !params.cart?.items.length
  ) {
    log("Not showing rescue panel: ", params);
    return false;
  }

  const now = Temporal.Now.instant();

  // If cart has more than 2 items and has been mounted for more than
  // 90 seconds (with 15 second jitter), show the panel
  const staleTime =
    STALE_PAGE_TIME_SECONDS +
    Math.floor(Math.random() * STALE_PAGE_TIME_JITTER_SECONDS);
  if (
    params.cart.items.reduce((acc, item) => acc + item.quantity, 0) > 2 &&
    now.since(params.mountTime).total({ unit: "seconds" }) > staleTime
  ) {
    return true;
  }

  const bucket = getDefaultCheckoutProbabilityBucket(
    Math.round(params.cumulativeConversionProbability * 100),
  );

  // Cold bucket trigger: if in COLD bucket for specified seconds, show panel
  // Since cumulative probability is non-decreasing, sessionAgeInMs equals time in cold
  if (
    bucket === DefaultCheckoutProbabilityBucket.COLD &&
    isDef(params.secondsInColdTrigger) &&
    isDef(params.sessionAgeInMs) &&
    params.sessionAgeInMs / MS_PER_SECOND >= params.secondsInColdTrigger
  ) {
    log("Showing rescue panel: cold bucket time trigger met");
    return true;
  }

  // Dropoff trigger only applies to cool and warm buckets
  if (
    bucket !== DefaultCheckoutProbabilityBucket.COOL &&
    bucket !== DefaultCheckoutProbabilityBucket.WARM
  ) {
    log("Should not show rescue panel, not in cool or warm bucket: ", bucket);
    return false;
  }

  // If within SHOW_DELAY_MS of mount, don't show
  if (
    now.since(params.mountTime).total({ unit: "milliseconds" }) < SHOW_DELAY_MS
  ) {
    log("Should not show rescue panel, within 3 seconds of mount time");
    return false;
  }

  // Show if instantaneous probability has dropped significantly
  const currentInstantaneousProb = params.instantaneousConversionProbability;
  const previousInstantaneousProb =
    params.previousInstantaneousConversionProbability;

  const shouldShow =
    currentInstantaneousProb <
    previousInstantaneousProb * (1 - params.dropoffPercentageTrigger / 100);
  log("Should show rescue panel result: ", shouldShow);
  return shouldShow;
};
